import { useMemo, useState, useEffect, useRef } from "react";
import KinAvatar from "./KinAvatar";
import SuggestionChip from "./SuggestionChip";
import PlanPicker from "./PlanPicker";


import SeeMoreChips from "./SeeMoreChips";
import PhotoIntakeModal from "./PhotoIntakeModal";
import PlantSnapModal from "./PlantSnapModal";
import {
  getRandomMealChip,
  getRandomSeasonalChip,
  getPersonalizedChipsFiltered,
  findChipByLabel,
  resolveChipPrompt,
  getTalkToKinChip,
  type ChipDef,
} from "@/constants/chipPools";
import { useFavorites } from "@/hooks/useFavorites";
import { useAuth } from "@/hooks/useAuth";
import { getFirstName } from "@/lib/userDisplayName";
import { trackFeature } from "@/lib/analytics";

const FRIDGE_PROMPT = "What can I make with what's in this photo? Look at what's actually visible (ingredients, leftovers, anything cookable) and suggest 2-3 realistic meal ideas using primarily what you see. Add only basic pantry staples if needed (salt, oil, garlic, etc.). Keep it practical, no fancy techniques. End by offering to expand any one into a full recipe with steps.";
const FRIDGE_DISPLAY = "What can I make with this? 📸";
const MAX_IMAGE_SIZE = 5 * 1024 * 1024;

const WHATS_IN_THIS_ANALYSIS_PROMPT = `The user has uploaded a photo of a product label, ingredient list, food packaging, supplement, cleaning product, or similar item. Analyze what's visible and give a plain-English breakdown a busy mom would actually find useful.

Your response must follow this structure — use these exact section headers:

**What is this?**
One sentence identifying the product or item.

**What's actually in it?**
Plain-English breakdown of the top ingredients or key components. Flag anything commonly known as a concern (high fructose corn syrup, parabens, fragrance/parfum, artificial dyes, etc.) in plain language — no fear-mongering, just facts. If it looks clean, say so. Keep this section to 5-7 bullet points max.

**The bottom line**
2-3 sentences. Is this something a mom would want to give her kids, use on her skin, use in her home? Give a real opinion. If there are better alternatives, briefly mention what to look for.

**One thing to know**
One short, memorable takeaway she can actually remember and use. Think: the thing she'd text her best friend after reading this.

Use casual, warm language. No jargon. If you cannot read the label clearly enough to analyze it, tell her what you can see and ask her to retake the photo with better lighting.`;

const DIAPER_ANALYSIS_PROMPT = `I'm uploading a photo of a diaper for you to look at.

Please describe what you see in neutral, factual terms — color, consistency, anything notable about appearance. Then list what would prompt a call to the pediatrician (for example: blood, very dark or black coloring, mucus, unusual color combinations, or anything that looks markedly different from the usual range).

You cannot diagnose from a photo. End your response by reminding me to call my pediatrician with any concerns — they can evaluate context (baby's age, recent feeding, illness) that you cannot see in a single photo.`;

const HOW_TO_MAKE_ANALYSIS_PROMPT = `The user uploaded a photo of something she'd love to recreate at home — it might be food (a dish she had at a restaurant, a bakery treat) or a craft/DIY project (something she saw on Pinterest, a decoration, a handmade thing).

First, look carefully at what's ACTUALLY in the photo. Only work from what you can genuinely see — never invent ingredients, materials, brand names, or details that aren't visible. Grounding matters more than being impressive.

Then respond based on what it is:

**If it's FOOD:**
Give her a realistic home-cook version.
- Open with one warm sentence naming what you see.
- **Ingredients** — a simple bulleted list of what she'd need.
- **How to make it** — plain, numbered steps. Skip fancy chef techniques unless the dish truly needs them.
- End by asking: "Want me to turn these ingredients into a grocery list?"

**If it's a CRAFT or DIY project:**
- Open with one warm sentence naming what you see.
- **What you'll need** — a bulleted materials list.
- **How to make it** — plain, numbered step-by-step instructions.

**If you genuinely can't tell what it is** (too blurry, too dark, or ambiguous):
Ask ONE specific clarifying question to pin down what she's trying to make. Don't guess, and don't give steps yet.

Keep it warm, encouraging, and down-to-earth — like a friend who's good at this walking her through it. No jargon.`;

const RASH_ANALYSIS_PROMPT = `I'm uploading a photo of a rash or skin concern for you to look at.

Please describe what you see in neutral, factual terms — color, texture, distribution, location if visible, whether it appears raised or flat. Then list monitoring criteria for when to call the pediatrician (for example: spreading rapidly, accompanied by fever, lethargy, swelling of face or throat, signs of infection like pus or warmth, or rash that doesn't blanch under pressure).

You cannot diagnose from a photo. End your response by reminding me to call my pediatrician with any medical concerns — they can evaluate the rash in person and consider history that you cannot see.`;

interface WelcomeScreenProps {
  onSendMessage: (
    message: string,
    imageDataOrDisplay?: { base64: string; mediaType: string; displayText?: string } | string
  ) => void;
  momTypes: string[] | null;
  momTypesLoading?: boolean;
  isPro?: boolean;
  onActivateVoiceMode?: () => void;
  /**
   * #1896 — she snapped a sign-up sheet. Deliberately NOT onSendMessage: the
   * photo goes to signup-sheet-parse, not to the model, and nothing about this
   * flow is a chat turn until Kin replies with what it filed.
   */
  onSignupSheet?: (images: { base64: string; mediaType: string }[]) => void;
}

const ChipSkeleton = () => (
  <div className="bg-muted rounded-xl px-4 py-3.5 animate-pulse">
    <div className="w-[22px] h-[22px] bg-accent rounded mb-1.5" />
    <div className="w-3/4 h-3 bg-accent rounded" />
  </div>
);

const WelcomeScreen = ({ onSendMessage, momTypes, momTypesLoading = false, isPro = false, onActivateVoiceMode, onSignupSheet }: WelcomeScreenProps) => {
  const { user } = useAuth();
  const [showSignupSnap, setShowSignupSnap] = useState(false);
  const firstName = getFirstName(user);
  const [showPlanPicker, setShowPlanPicker] = useState(false);
  const [showWhatsInThis, setShowWhatsInThis] = useState(false);
  const [showHowToMake, setShowHowToMake] = useState(false);
  const [showDiaperSnap, setShowDiaperSnap] = useState(false);
  const [showRashSnap, setShowRashSnap] = useState(false);
  const [showPlantSnap, setShowPlantSnap] = useState(false);
  const [chipsReady, setChipsReady] = useState(false);
  const fridgeInputRef = useRef<HTMLInputElement>(null);

  const handleFridgeFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";
    if (file.size > MAX_IMAGE_SIZE) return;
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const base64 = result.split(",")[1];
      const mediaType = file.type || "image/jpeg";
      onSendMessage(FRIDGE_PROMPT, { base64, mediaType, displayText: FRIDGE_DISPLAY });
    };
    reader.readAsDataURL(file);
  };

  const handleChipClick = (prompt: string, label?: string) => {
    if (label) {
      trackFeature("chip_tap", { chip_label: label, source: "home" });
      const lower = label.toLowerCase();
      if (lower.includes("vent")) trackFeature("vent_mode", { source: "chip", chip_label: label });
      if (lower.includes("brain dump")) trackFeature("brain_dump", { source: "chip", chip_label: label });
    }
    try {
      if (!sessionStorage.getItem("kin_first_chip_tapped")) {
        sessionStorage.setItem("kin_first_chip_tapped", "true");
        window.dispatchEvent(new Event("kin:first-chip-tapped"));
      }
    } catch {
      // private browsing, fail silent
    }
    if (prompt === "__TALK_TO_KIN__") {
      if (isPro) {
        onActivateVoiceMode?.();
      } else {
        onSendMessage("Tell me about voice mode — I tapped the Talk to Kin chip but I'm not a Pro subscriber yet.");
      }
      return;
    }

    if (prompt === "__OPEN_WHATS_IN_THIS__") {
      setShowWhatsInThis(true);
      return;
    }
    if (prompt === "__OPEN_SIGNUP_SNAP__") {
      trackFeature("signup_sheet_snap");
      setShowSignupSnap(true);
      return;
    }
    if (prompt === "__OPEN_HOW_TO_MAKE__") {
      setShowHowToMake(true);
      return;
    }
    if (prompt === "__OPEN_DIAPER_SNAP__") {
      setShowDiaperSnap(true);
      return;
    }
    if (prompt === "__OPEN_RASH_SNAP__") {
      setShowRashSnap(true);
      return;
    }
    if (prompt === "__OPEN_PLANT_SNAP__") {
      setShowPlantSnap(true);
      return;
    }
    if (prompt === "__OPEN_FRIDGE_SNAP__") {
      fridgeInputRef.current?.click();
      return;
    }
    const finalPrompt = label ? resolveChipPrompt(label, prompt) : prompt;
    onSendMessage(finalPrompt);
  };

  const { favorites, toggleFavorite } = useFavorites();

  const mealChip = useMemo(() => getRandomMealChip(momTypes || undefined), [momTypes]);
  /**
   * null in any month with no seasonal chip (issue #593) — the slot is simply
   * dropped and a personalized chip backfills it, so the grid keeps its size.
   * A stale "Summer bucket list" in slot 2 in November was the loudest version
   * of this bug: prime real estate, and it reads as Kin not knowing the month.
   */
  const seasonalChip = useMemo(() => getRandomSeasonalChip(), []);

  const favoriteChips: ChipDef[] = useMemo(() => {
    const seen = new Set<string>([mealChip.label, seasonalChip?.label].filter(Boolean) as string[]);
    const out: ChipDef[] = [];
    for (const label of favorites) {
      if (seen.has(label)) continue;
      const def = findChipByLabel(label);
      if (def) {
        out.push(def);
        seen.add(label);
      }
    }
    return out.slice(0, 3);
  }, [favorites, mealChip, seasonalChip]);

  const personalizedChips = useMemo(() => {
    const exclude = new Set<string>([
      mealChip.label,
      ...(seasonalChip ? [seasonalChip.label] : []),
      ...favoriteChips.map((c) => c.label),
    ]);
    // One extra personalized chip backfills the empty seasonal slot, so `base`
    // below is the same length in and out of season and the slices below it
    // behave identically either way.
    const remaining = (seasonalChip ? 6 : 7) - favoriteChips.length;
    return getPersonalizedChipsFiltered(momTypes || [], exclude, remaining);
  }, [momTypes, mealChip, seasonalChip, favoriteChips]);

  const allChips = useMemo(() => {
    const base = [
      mealChip,
      ...(seasonalChip ? [seasonalChip] : []),
      ...favoriteChips,
      ...personalizedChips,
    ];
    const talkChip = getTalkToKinChip();
    if (isPro) {
      // Pro: Talk to Kin first, keep max 8 total chips
      return [talkChip, ...base.slice(0, 7)];
    }
    // Free: Talk to Kin at position 5 (visible but not prime real estate), keep max 8
    const pos = Math.min(5, base.length);
    return [...base.slice(0, pos), talkChip, ...base.slice(pos, 7)];
  }, [mealChip, seasonalChip, favoriteChips, personalizedChips, isPro]);

  const shownLabels = useMemo(
    () => new Set(allChips.map((c) => c.label)),
    [allChips]
  );

  const favoriteSet = useMemo(() => new Set(favorites), [favorites]);

  useEffect(() => {
    console.log("[CHIPS] Assembling array...");
    console.log(`[CHIPS] Meal chip: ${mealChip.label}, favorites: ${favoriteChips.length}, personalized: ${personalizedChips.length}, total: ${allChips.length}`);
    const t = setTimeout(() => setChipsReady(true), 150);
    return () => clearTimeout(t);
  }, [mealChip, favoriteChips, personalizedChips, allChips]);


  return (
    <div className="flex flex-col items-center px-4 pt-6 pb-6 max-w-lg mx-auto short:pt-4 mt-auto w-full">
      {/* Logo — smaller on short screens */}
      <div className="animate-float mb-4 short:mb-2">
        <div className="hidden short:block">
          <KinAvatar size="md" variant="dark" />
        </div>
        <div className="block short:hidden">
          <KinAvatar size="lg" variant="dark" />
        </div>
      </div>

      <h2
        className="font-display text-foreground text-center animate-stagger-in short:text-[24px]"
        style={{ fontSize: "32px", fontWeight: 400, letterSpacing: "-0.5px", color: "#1C1A3E", animationDelay: "100ms", marginBottom: "2px" }}
      >
        Hey {firstName || "mama"}, <span className="font-serif italic" style={{ color: "#5050C8" }}>I'm Kin.</span>
      </h2>
      <p
        className="font-body text-center animate-stagger-in"
        style={{ fontSize: "13px", fontWeight: 400, color: "#54525C", lineHeight: 1.4, maxWidth: "380px", animationDelay: "200ms" }}
      >
        Your AI-powered mom brain, here to help you do it all.
      </p>
      <p
        className="font-body text-center mb-4 short:mb-3 animate-stagger-in"
        style={{ fontSize: "13px", fontWeight: 400, color: "#5050C8", lineHeight: 1.4, maxWidth: "380px", animationDelay: "250ms" }}
      >
        Not sure where to start? Just tell me what today looks like.
      </p>


      {showPlanPicker ? (
        <div className="w-full mb-4">
          <PlanPicker
            onSendMessage={(msg, display) => {
              setShowPlanPicker(false);
              onSendMessage(msg, display);
            }}
            onDismiss={() => setShowPlanPicker(false)}
          />
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-x-2 gap-y-2.5 w-full mb-1">
          {chipsReady
            ? allChips.map((chip, i) => (
                <SuggestionChip
                  key={chip.label}
                  emoji={chip.emoji}
                  label={chip.label}
                  onClick={() => handleChipClick(chip.prompt, chip.label)}
                  delay={i * 120}
                  isFavorited={favoriteSet.has(chip.label)}
                  onToggleFavorite={() => toggleFavorite(chip.label)}
                  isProFeature={chip.isProFeature}
                />
              ))
            : Array.from({ length: 8 }).map((_, i) => (
                <ChipSkeleton key={i} />
              ))
          }
        </div>
      )}

      {!showPlanPicker && chipsReady && (
        <SeeMoreChips
          shownLabels={shownLabels}
          currentMealChip={mealChip}
          onSendMessage={handleChipClick}
          favoriteSet={favoriteSet}
          onToggleFavorite={toggleFavorite}
        />
      )}


      {showSignupSnap && (
        <PhotoIntakeModal
          title="Snap a sign-up sheet"
          subtitle="Snap or upload the sheet. Kin files what you signed up for."
          disclaimer="One sheet at a time, in one photo. Get the whole sheet in the frame, names and all."
          analysisPrompt=""
          displayText="Snap a sign-up sheet 📋"
          ctaText="File it 📋"
          onClose={() => setShowSignupSnap(false)}
          onAnalyze={(_apiPrompt, imageData, _displayText, allImages) => {
            setShowSignupSnap(false);
            // ⚠️ NOT onSendMessage. These photos go to signup-sheet-parse, not
            // to the model, so they must never become a chat turn carrying an
            // image of other families' names.
            //
            // ONE photo (Nov 2 2026). Two photos failed on her phone, and two
            // different sheets in one read cannot be filed as one sheet. The
            // function still accepts images[]; the modal just offers one.
            const images = (allImages && allImages.length > 0 ? allImages : [imageData])
              .map((im) => ({ base64: im.base64, mediaType: im.mediaType }));
            onSignupSheet?.(images);
          }}
        />
      )}

      {showWhatsInThis && (
        <PhotoIntakeModal
          title="What's in this?"
          subtitle="Snap or upload a photo of any label, ingredient list, or product."
          analysisPrompt={WHATS_IN_THIS_ANALYSIS_PROMPT}
          displayText="What's in this? 🔍"
          onClose={() => setShowWhatsInThis(false)}
          onAnalyze={(apiPrompt, imageData, displayText) => {
            setShowWhatsInThis(false);
            onSendMessage(apiPrompt, { ...imageData, displayText });
          }}
        />
      )}

      {showHowToMake && (
        <PhotoIntakeModal
          title="How do I make this? 📸"
          subtitle="Snap a pic of anything — sushi you loved, a craft you saw, something from Pinterest — and I'll figure out how you can make it at home."
          analysisPrompt={HOW_TO_MAKE_ANALYSIS_PROMPT}
          displayText="How do I make this? 📸"
          ctaText="Show me how 📸"
          onClose={() => setShowHowToMake(false)}
          onAnalyze={(apiPrompt, imageData, displayText) => {
            setShowHowToMake(false);
            onSendMessage(apiPrompt, { ...imageData, displayText });
          }}
        />
      )}

      {showDiaperSnap && (
        <PhotoIntakeModal
          title="Diaper check"
          subtitle="Snap or upload a photo and Kin will describe what it shows."
          disclaimer="Kin describes what the photo shows, but can't diagnose. Call your pediatrician for any concerns."
          analysisPrompt={DIAPER_ANALYSIS_PROMPT}
          displayText="Diaper check 🧷"
          onClose={() => setShowDiaperSnap(false)}
          onAnalyze={(apiPrompt, imageData, displayText) => {
            setShowDiaperSnap(false);
            onSendMessage(apiPrompt, { ...imageData, displayText });
          }}
        />
      )}

      {showRashSnap && (
        <PhotoIntakeModal
          title="Rash check"
          subtitle="Snap or upload a photo and Kin will describe what it shows."
          disclaimer="Kin describes what the photo shows, but can't diagnose from a photo. Call your pediatrician for any medical concerns."
          analysisPrompt={RASH_ANALYSIS_PROMPT}
          displayText="Rash check 🩹"
          onClose={() => setShowRashSnap(false)}
          onAnalyze={(apiPrompt, imageData, displayText) => {
            setShowRashSnap(false);
            onSendMessage(apiPrompt, { ...imageData, displayText });
          }}
        />
      )}

      {showPlantSnap && (
        <PlantSnapModal
          onClose={() => setShowPlantSnap(false)}
          onAnalyze={(apiPrompt, imageData, displayText) => {
            setShowPlantSnap(false);
            onSendMessage(apiPrompt, { ...imageData, displayText });
          }}
        />
      )}

      <input
        ref={fridgeInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleFridgeFile}
        className="hidden"
      />
    </div>
  );
};

export default WelcomeScreen;
